import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import { TooltipModel } from '@notino/react-styleguide';
import { IStock } from '@notino/shared/definitions/custom-definitions';
import {
  AvailabilityState,
  CatalogVariantFragmentFragment,
  Roles,
} from '@notino/shared/definitions/types';

import { PortalTooltip } from '@components/PortalTooltip';
import { useFormatPrice } from '@components/PriceLabel/useFormattedPrice';
import { ILocale } from '@containers/App/model';
import {
  IDamage,
  MaximumVolumeInPercent,
} from '@containers/ProductListing/model';
import { useUser } from '@queries/useUser';

import messages from '../../../messages';
import { useNewPdDesignEnabled } from '../../hooks/useNewPdDesignEnabled';
import { usePriceLogic } from '../../hooks/usePriceLogic';
import { PriceRatio } from '../VariantsInSelectBox/PriceRatio';

import {
  Wrapper,
  CodeBlock,
  CodeName,
  Separator,
  Availability,
  AdditionalDescriptionStyled,
  AdditionalText,
  StyledInfoIcon,
} from './styled';

const Damage: React.FC<{ damage: IDamage }> = ({ damage }) => {
  const message =
    damage.volumeInPercent === MaximumVolumeInPercent
      ? messages.productDamaged
      : messages.productReturned;

  return (
    <AdditionalDescriptionStyled highlight={true}>
      <FormattedMessage {...message} />
    </AdditionalDescriptionStyled>
  );
};

interface IBelowTheLineProps {
  variant: CatalogVariantFragmentFragment;
  locale: ILocale;
}

export const BellowTheLine: React.FC<IBelowTheLineProps> = React.memo(
  ({ variant, locale }) => {
    const {
      attributes,
      orderCode,
      productCode,
      webId,
      catalogId,
      annotation,
      additionalText,
      availability,
      stockAvailability,
    } = variant;

    const { user } = useUser();

    const { bottomRecentPrice } = usePriceLogic(variant);
    const formatPrice = useFormatPrice();
    const newDesign = useNewPdDesignEnabled();

    const isImmediateCollection =
      availability.availablePickUpStores > 0 &&
      availability.state !== AvailabilityState.CanBeBought;

    const isMaster = attributes && attributes.Master;

    return (
      <Wrapper newDesign={newDesign}>
        <Availability
          newDesign={newDesign}
          highlight={[IStock.outOfStock, IStock.lessThan5].includes(
            stockAvailability.code as IStock
          )}
        >
          {isImmediateCollection ? (
            <FormattedMessage {...messages.immediateCollection} />
          ) : (
            <FormattedMessage
              {...messages[stockAvailability.code]}
              values={{
                count: stockAvailability.count,
              }}
            />
          )}
        </Availability>

        {variant.unitPrice && (
          <PriceRatio
            prepend={<Separator newDesign={newDesign}> | </Separator>}
            stockAvailability={variant.stockAvailability.code}
            unitAmount={variant.unitPrice.perAmount}
            locale={locale}
            unit={variant.unitPrice.unit}
            unitPrice={variant.unitPrice}
          />
        )}

        {bottomRecentPrice && (
          <>
            <Separator newDesign={newDesign}> | </Separator>
            <FormattedMessage {...messages.minimumRecentPrice} />{' '}
            {formatPrice(bottomRecentPrice.value)?.formattedPriceWithCurrency}
            <PortalTooltip
              interactive={true}
              position={TooltipModel.Position.topLeft}
              content={
                <PortalTooltip.Content>
                  {bottomRecentPrice.tooltip}
                </PortalTooltip.Content>
              }
            >
              <StyledInfoIcon color="icon.disabled" height="0.9em" />
            </PortalTooltip>
          </>
        )}

        <Separator newDesign={newDesign}> | </Separator>

        <CodeBlock>
          <CodeName>
            <FormattedMessage {...messages.code} />{' '}
          </CodeName>

          {user.role === Roles.Admin ? (
            <span>
              <span role="button">{orderCode}</span> <b>{productCode}</b>{' '}
              {webId} {catalogId}
              {isMaster && <b> M</b>}
            </span>
          ) : (
            orderCode
          )}
        </CodeBlock>

        {user.role === Roles.Admin && attributes.EAN && (
          <AdditionalDescriptionStyled>
            EAN: {attributes.EAN}
          </AdditionalDescriptionStyled>
        )}

        {attributes.IsForProfessionals && (
          <AdditionalDescriptionStyled highlight={true}>
            <FormattedMessage {...messages.isForProfessionals} />
          </AdditionalDescriptionStyled>
        )}

        {attributes.Damage && <Damage damage={attributes.Damage} />}

        {attributes.WithoutCellophane && (
          <AdditionalDescriptionStyled>
            <FormattedMessage {...messages.withoutCellophane} />
          </AdditionalDescriptionStyled>
        )}

        {attributes.DifferentPackaging && (
          <AdditionalDescriptionStyled>
            <FormattedMessage {...messages.differentPackaging} />
          </AdditionalDescriptionStyled>
        )}

        {annotation && annotation.length > 0 && (
          <AdditionalDescriptionStyled>
            <span>{annotation}</span>
          </AdditionalDescriptionStyled>
        )}

        {additionalText && (
          <AdditionalText
            dangerouslySetInnerHTML={{ __html: additionalText }}
          />
        )}
      </Wrapper>
    );
  }
);
